import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ScrollableCellWithColorDot } from '@/components/data-table/cells';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/modules/common/components/molecules/date/date-picker';
import { DateRangePicker } from '@/modules/common/components/molecules/date/date-range-picker';
import {
  SingleSelect,
  SingleSelectOption,
} from '@/modules/common/components/molecules/selectors/single-select';
import { formatDisplayDate } from '@/utils/date-formatters';

import { InvoiceDetails } from '../../types';
import { UpdateInvoiceFormValues } from '../../validations';
import { InvoiceClientField } from './invoice-client-field';

interface InvoiceGeneralInformationCardProps {
  details: InvoiceDetails;
  form: UseFormReturn<UpdateInvoiceFormValues>;
  isReadOnly: boolean;
  noValue: string;
  dueDate?: Date | null;
  currencyOptions: SingleSelectOption[];
  isLoadingCurrencies: boolean;
  paymentTermOptions: SingleSelectOption[];
}

/** Groups the editable invoice metadata fields with their read-only project and client context. */
export function InvoiceGeneralInformationCard({
  details,
  form,
  isReadOnly,
  noValue,
  dueDate,
  currencyOptions,
  isLoadingCurrencies,
  paymentTermOptions,
}: InvoiceGeneralInformationCardProps) {
  const { t } = useTranslation(['authenticated.invoices', 'common']);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('details.generalInformation')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <Label className="flex flex-col items-stretch gap-2">
            <span>{t('details.fields.project')}</span>
            {details.project ? (
              <div className="flex min-h-10 items-center rounded-md border border-input bg-muted px-3 py-1 text-sm">
                <ScrollableCellWithColorDot
                  text={details.project.name}
                  image={details.project.logoUrl}
                  avatarName={details.project.name}
                  avatarSize="sm"
                  alt={`${details.project.name} logo`}
                  dotColor={details.project.colorHex || 'hsl(var(--muted-foreground))'}
                  withAvatarBorder
                  className="w-full"
                />
              </div>
            ) : (
              <Input value={noValue} disabled />
            )}
          </Label>
          <InvoiceClientField client={details.client} label={t('details.fields.client')} />
          <SingleSelect
            name="currencyCode"
            control={form.control}
            label={t('details.fields.currency')}
            placeholder={t('details.edit.placeholders.currency')}
            options={currencyOptions}
            disabled={isReadOnly}
            loading={isLoadingCurrencies}
          />
          <DateRangePicker
            name="period"
            control={form.control}
            label={t('filters.billingPeriod')}
            triggerClassName="truncate"
            disabled={isReadOnly}
          />
          <SingleSelect
            name="paymentTerm"
            control={form.control}
            label={t('details.fields.paymentTerms')}
            placeholder={t('details.edit.placeholders.paymentTerm')}
            options={paymentTermOptions}
            disabled={isReadOnly}
          />
          <Label className="flex flex-col items-stretch gap-2">
            <span>{t('details.fields.dueDate')}</span>
            <Input
              value={
                isReadOnly
                  ? formatDisplayDate(details.dueDate)
                  : dueDate
                    ? formatDisplayDate(dueDate)
                    : noValue
              }
              disabled
              className="text-sm"
            />
          </Label>
          <DatePicker
            name="issuedDate"
            control={form.control}
            label={t('details.fields.issuedDate')}
            captionLayout="dropdown"
            inputDisabled={isReadOnly}
          />
        </div>
      </CardContent>
    </Card>
  );
}
